import { useEffect } from 'react';
import { Outlet, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HotkeysProvider } from 'react-hotkeys-hook';
import { AuthProvider, useAuth } from './store/auth-context';
import { ThemeProvider, useTheme } from './store/theme-context';
import { AudioThemeProvider } from './store/audio-theme-context';
import { ToastProvider } from './components/ui/Toast';
import { KeyboardShortcutsHelp } from './components/keyboard/KeyboardShortcutsHelp';
import { SurfaceToggleHotkey } from './components/keyboard/SurfaceToggleHotkey';
import { EventDebugHud } from './components/debug/EventDebugHud';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { PlatformEventProvider } from './contexts/PlatformEventContext';
import { ConversationNamedEventBridge } from './hooks/useConversationNamedEvent';
import { SpaceProvider, useSpaceContext } from './contexts/SpaceContext';
import { useMeSpaces } from './hooks/useMeSpaces';
import { resolveDefaultSpaceId } from './lib/default-space';
import OperatorLayout from './components/layout/OperatorLayout';
import ConsoleLayout from './components/layout/ConsoleLayout';
import SandboxPage from './pages/sandbox/SandboxPage';
import SignIn from './pages/auth/SignIn';
import SignInReset from './pages/auth/SignInReset';
import SignUp from './pages/auth/SignUp';
import ForgotPassword from './pages/auth/ForgotPassword';
import OAuthConnected from './pages/auth/OAuthConnected';
import SsoLanding from './pages/auth/SsoLanding';
import DeviceSignIn from './pages/auth/DeviceSignIn';
import DeviceApprovePage from './pages/auth/DeviceApprovePage';
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import OperatorInbox from './pages/operator/OperatorInbox';
import OperatorOverview from './pages/operator/OperatorOverview';
import ChatList from './pages/operator/ChatList';
import NewChatPage from './pages/conversations/NewChatPage';
import AgentList from './pages/agents/AgentList';
import AgentDetail from './pages/agents/AgentDetail';
import AgentForm from './pages/agents/AgentForm';
import SkillList from './pages/skills/SkillList';
import SkillDetail from './pages/skills/SkillDetail';
import PluginList from './pages/plugins/PluginList';
import PluginDetail from './pages/plugins/PluginDetail';
import SkillForm from './pages/skills/SkillForm';
import ToolList from './pages/tools/ToolList';
import ToolDetail from './pages/tools/ToolDetail';
import ToolForm from './pages/tools/ToolForm';
import GatewayDetail from './pages/tools/gateways/GatewayDetail';
import McpServerForm from './pages/tools/mcp/McpServerForm';
import McpServerDetail from './pages/tools/mcp/McpServerDetail';
import OrgMcpServerView from './pages/tools/mcp/OrgMcpServerView';
import KnowledgeBasesPage from './pages/knowledge/KnowledgeBasesPage';
import SpaceKnowledgeBasesPage from './pages/knowledge/SpaceKnowledgeBasesPage';
import EmailList from './pages/connections/EmailList';
import EmailDetail from './pages/connections/EmailDetail';
import EmailForm from './pages/connections/EmailForm';
import SettingsPage from './pages/settings/SettingsPage';
import CredentialsPage from './pages/credentials/CredentialsPage';
import IntegrationList from './pages/integrations/IntegrationList';
import IntegrationDetail from './pages/integrations/IntegrationDetail';
import IntegrationForm from './pages/integrations/IntegrationForm';
import SubscriptionDetail from './pages/integrations/SubscriptionDetail';
import OrgIntegrationList from './pages/admin/OrgIntegrationList';
import OrgIntegrationForm from './pages/admin/OrgIntegrationForm';
import OrgIntegrationDetail from './pages/admin/OrgIntegrationDetail';
import GoogleConnect from './pages/integrations/GoogleConnect';
import GoogleWorkspacePage from './pages/admin/GoogleWorkspacePage';
import OrgMcpServerList from './pages/admin/OrgMcpServerList';
import OrgMcpServerForm from './pages/admin/OrgMcpServerForm';
import OrgMcpServerDetail from './pages/admin/OrgMcpServerDetail';
import OrgAdminLayout from './pages/admin/OrgAdminLayout';
import OrgPluginCatalogConfig from './pages/admin/OrgPluginCatalogConfig';
import OrgProvidersConfig from './pages/admin/OrgProvidersConfig';
import OrgKnowledgeBasesPage from './pages/admin/OrgKnowledgeBasesPage';
import OrgAnalyticsPage from './pages/admin/OrgAnalyticsPage';
import RoutingRuleForm from './pages/admin/RoutingRuleForm';
import RequireOrgAdmin from './components/auth/RequireOrgAdmin';
import EventStreamPage from './pages/events/EventStreamPage';
import ActivityPage from './pages/activity/ActivityPage';
import { RunsPage } from './pages/runs';
import ConversationList from './pages/conversations/ConversationList';
import SpaceManagePage from './pages/spaces/SpaceManagePage';
import { ForbiddenSpace } from './pages/spaces/ForbiddenSpace';
import MarkdownTestPage from './pages/dev/MarkdownTestPage';
import MarkdownEditorTestPage from './pages/dev/MarkdownEditorTestPage';
import AuiShowcasePage from './pages/dev/AuiShowcasePage';
import AuiFilesTestPage from './pages/dev/AuiFilesTestPage';
import ConversationPreviewPage from './pages/dev/ConversationPreviewPage';
import ConversationScrollPage from './pages/dev/ConversationScrollPage';
import CotPlaybackPage from './pages/dev/CotPlaybackPage';
import ResumeRunPage from './pages/dev/ResumeRunPage';
import ChartShowcasePage from './pages/dev/ChartShowcasePage';
import AnalyticsShowcasePage from './pages/dev/AnalyticsShowcasePage';
import CalendarShowcasePage from './pages/dev/CalendarShowcasePage';
import CanvasPanelPreviewPage from './pages/dev/CanvasPanelPreviewPage';
import CredentialsPreviewPage from './pages/dev/CredentialsPreviewPage';
import AuthShowcasePage from './pages/dev/AuthShowcasePage';
import SpinnerShowcasePage from './pages/dev/SpinnerShowcasePage';
import ProviderLogoShowcasePage from './pages/dev/ProviderLogoShowcasePage';
import SidebarPreviewPage from './pages/dev/SidebarPreviewPage';
import ProfilePicturePreviewPage from './pages/dev/ProfilePicturePreviewPage';
import InlineDiffPreviewPage from './pages/dev/InlineDiffPreviewPage';
import AskUserShowcasePage from './pages/dev/AskUserShowcasePage';
import VoiceSessionBarPreviewPage from './pages/dev/VoiceSessionBarPreviewPage';
import VoiceCaptureCheckPage from './pages/dev/VoiceCaptureCheckPage';
import VoicePlaybackCheckPage from './pages/dev/VoicePlaybackCheckPage';
import DevIndexPage from './pages/dev/DevIndexPage';
import { ErrorBoundary } from './components/ErrorBoundary';

/**
 * Layout-only route that wraps every space-scoped console page in
 * `SpaceProvider`. The provider reads `:spaceId` from `useParams` and pairs
 * it with the user's space list so child pages can render attribution.
 *
 * Also mounts `SandboxPage` persistently inside the provider so the user's
 * sandbox conversation survives navigation between tabs within a space.
 */
function SpaceProviderRoute() {
  const { pathname } = useLocation();
  const isSandbox = /^\/c\/s\/[^/]+\/sandbox(\/.*)?$/.test(pathname);
  return (
    <SpaceProvider>
      <SpaceAccessGate isSandbox={isSandbox} />
    </SpaceProvider>
  );
}

/**
 * Sits inside `SpaceProvider` so it can read `useSpaceContext`. When the
 * active space id doesn't resolve against the user's `/me/spaces`, render the
 * forbidden state instead of mounting child routes — those would otherwise
 * fire backend calls scoped to a space the user can't access and surface
 * misleading errors.
 */
function SpaceAccessGate({ isSandbox }: { isSandbox: boolean }) {
  const { isLoading, isForbidden } = useSpaceContext();
  if (isLoading) return null;
  if (isForbidden) return <ForbiddenSpace />;
  return (
    <>
      <Outlet />
      <SandboxPage visible={isSandbox} />
    </>
  );
}

/**
 * Redirects /console (no space) to the user's last-visited space, falling
 * back to the user's Personal Space, then the first available space, and
 * finally to the operator surface at `/` if the user has none.
 */
function ConsoleDefaultSpaceRedirect() {
  const { user, isLoading } = useAuth();
  const { data, isLoading: spacesLoading } = useMeSpaces();
  if (isLoading || spacesLoading) return null;
  const target = resolveDefaultSpaceId({
    spaces: data?.items ?? [],
    lastSpaceId: user?.preferences?.lastSpaceId,
    defaultSpaceId: user?.defaultSpaceId,
  });
  if (target) {
    return <Navigate to={`/c/s/${target}/agents`} replace />;
  }
  return <Navigate to="/" replace />;
}

/** Syncs the user's saved theme preference into ThemeProvider on login. */
function ThemeSyncer() {
  const { user, updatePreferences } = useAuth();
  const { mode, setMode } = useTheme();

  useEffect(() => {
    const savedTheme = user?.preferences?.theme;
    if (savedTheme && savedTheme !== mode) {
      setMode(savedTheme);
    }
  }, [user?.preferences?.theme]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    const savedTheme = user.preferences?.theme;
    if (savedTheme !== mode) {
      updatePreferences({ theme: mode }).catch(() => {});
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

export default function App() {
  return (
    <ThemeProvider>
      <HotkeysProvider>
        <AudioThemeProvider>
          <AuthProvider>
            <ToastProvider>
              <ThemeSyncer />
              <KeyboardShortcutsHelp />
              <SurfaceToggleHotkey />
              <ErrorBoundary>
              <Routes>
              {/* Public routes */}
              <Route path="/sso" element={<SsoLanding />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signin/reset" element={<SignInReset />} />
              {/* Cross-device login: /signin/device runs on the consuming
                  screen (car/TV); /device is the phone's approval page
                  (self-guarding — bounces unauthenticated visitors through
                  /signin with the deep link preserved). */}
              <Route path="/signin/device" element={<DeviceSignIn />} />
              <Route path="/device" element={<DeviceApprovePage />} />
              {import.meta.env.VITE_SIGNUP_ENABLED === 'true' && (
                <Route path="/signup" element={<SignUp />} />
              )}
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/oauth/connected" element={<OAuthConnected />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />

              {/* Dev-only public routes */}
              {import.meta.env.DEV && (
                <Route path="/dev" element={<DevIndexPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/markdown-test" element={<MarkdownTestPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/markdown-editor-test" element={<MarkdownEditorTestPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/aui-showcase" element={<AuiShowcasePage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/aui-files-test" element={<AuiFilesTestPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/conversation-preview" element={<ConversationPreviewPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/conversation-scroll" element={<ConversationScrollPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/cot-playback" element={<CotPlaybackPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/resume-run" element={<ResumeRunPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/chart-showcase" element={<ChartShowcasePage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/analytics-showcase" element={<AnalyticsShowcasePage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/calendar-showcase" element={<CalendarShowcasePage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/credentials-preview" element={<CredentialsPreviewPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/auth-showcase" element={<AuthShowcasePage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/canvas-panel-preview" element={<CanvasPanelPreviewPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/spinner-showcase" element={<SpinnerShowcasePage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/provider-logo-showcase" element={<ProviderLogoShowcasePage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/profile-picture-preview" element={<ProfilePicturePreviewPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/sidebar-preview" element={<SidebarPreviewPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/inline-diff-preview" element={<InlineDiffPreviewPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/ask-user-showcase" element={<AskUserShowcasePage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/voice-session-bar" element={<VoiceSessionBarPreviewPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/voice-capture-check" element={<VoiceCaptureCheckPage />} />
              )}
              {import.meta.env.DEV && (
                <Route path="/dev/voice-playback-check" element={<VoicePlaybackCheckPage />} />
              )}

              {/* Operator (primary surface) — mounted at root */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <PlatformEventProvider>
                      <ConversationNamedEventBridge />
                      <OperatorLayout />
                    </PlatformEventProvider>
                  </ProtectedRoute>
                }
              >
                <Route index element={<OperatorOverview />} />
                <Route path="inbox" element={<OperatorInbox />} />
                <Route path="chat" element={<ChatList />} />
                <Route path="chat/new" element={<NewChatPage />} />
                {/* Chat detail rendered persistently by OperatorPage in OperatorLayout */}
                <Route path="chat/:conversationId" element={null} />
                <Route path="knowledge" element={<KnowledgeBasesPage />} />
                <Route path="knowledge/:kbId" element={<KnowledgeBasesPage />} />
                <Route path="knowledge/:kbId/p/*" element={<KnowledgeBasesPage />} />
                <Route path="knowledge/:kbId/f/:fileId" element={<KnowledgeBasesPage />} />
                <Route path="activity" element={<ActivityPage />} />
                {/* Builder rendered persistently in OperatorLayout */}
                <Route path="builder" element={null} />
              </Route>

              {/* Console (`/c`) */}
              <Route
                path="/c"
                element={
                  <ProtectedRoute>
                    <PlatformEventProvider>
                      <ConversationNamedEventBridge />
                      <ConsoleLayout />
                    </PlatformEventProvider>
                  </ProtectedRoute>
                }
              >
                <Route index element={<ConsoleDefaultSpaceRedirect />} />

                {/* Cross-space console pages (no spaceId in URL) */}
                <Route path="events" element={<EventStreamPage />} />
                <Route path="runs" element={<RunsPage />} />
                <Route path="r/:runId" element={<RunsPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="credentials" element={<CredentialsPage />} />
                {/* Admin-only usage analytics. Guarded on its own since it
                    sits outside the `/c/org` RequireOrgAdmin subtree. */}
                <Route
                  path="analytics"
                  element={
                    <RequireOrgAdmin>
                      <OrgAnalyticsPage />
                    </RequireOrgAdmin>
                  }
                />

                {/* Org-admin pages — `/c/org/...`. Guarded by RequireOrgAdmin
                    in addition to the surrounding ProtectedRoute. */}
                <Route
                  path="org"
                  element={
                    <RequireOrgAdmin>
                      <Outlet />
                    </RequireOrgAdmin>
                  }
                >
                  <Route index element={<Navigate to="integrations" replace />} />
                  {/* List routes share the OrgAdminLayout tab strip. Detail
                      and form routes sit outside the layout so the tabs aren't
                      shown when the user has drilled into a specific entity. */}
                  <Route element={<OrgAdminLayout />}>
                    <Route path="integrations" element={<OrgIntegrationList />} />
                    <Route path="mcp-servers" element={<OrgMcpServerList />} />
                    <Route path="plugin-catalog" element={<OrgPluginCatalogConfig />} />
                    <Route path="identity-providers" element={<OrgProvidersConfig />} />
                    <Route path="kbs" element={<OrgKnowledgeBasesPage />} />
                    <Route path="google-workspace" element={<GoogleWorkspacePage />} />
                  </Route>
                  <Route path="integrations/new" element={<OrgIntegrationForm />} />
                  <Route path="i/:integrationId" element={<OrgIntegrationDetail />} />
                  <Route
                    path="i/:integrationId/rules/new"
                    element={<RoutingRuleForm />}
                  />
                  <Route path="mcp-servers/new" element={<OrgMcpServerForm />} />
                  <Route path="mcp/:serverId" element={<OrgMcpServerDetail />} />
                  <Route path="mcp/:serverId/edit" element={<OrgMcpServerForm />} />
                  <Route path="kbs/:kbId" element={<OrgKnowledgeBasesPage />} />
                  <Route path="kbs/:kbId/p/*" element={<OrgKnowledgeBasesPage />} />
                  <Route path="kbs/:kbId/f/:fileId" element={<OrgKnowledgeBasesPage />} />
                </Route>

                {/* Space-scoped console pages — `/c/s/:spaceId/...` */}
                <Route path="s/:spaceId" element={<SpaceProviderRoute />}>
                  <Route index element={<Navigate to="agents" replace />} />

                  <Route path="agents" element={<AgentList />} />
                  <Route path="agents/schedules" element={<AgentList />} />
                  <Route path="agents/subscriptions" element={<AgentList />} />
                  <Route path="agents/new" element={<AgentForm />} />
                  <Route path="a/:agentId" element={<AgentDetail />} />
                  <Route path="a/:agentId/edit" element={<AgentForm />} />

                  <Route path="skills" element={<SkillList />} />
                  <Route path="skills/new" element={<SkillForm />} />
                  <Route path="sk/:skillId" element={<SkillDetail />} />
                  <Route path="sk/:skillId/edit" element={<SkillForm />} />

                  <Route path="plugins" element={<PluginList />} />
                  <Route path="plugins/new" element={<PluginList openInstallOnMount />} />
                  <Route path="plg/:pluginId" element={<PluginDetail />} />

                  <Route path="tools" element={<ToolList />} />
                  <Route path="tools/gateways" element={<ToolList />} />
                  <Route path="tools/gw/:gatewayId" element={<GatewayDetail />} />
                  <Route path="tools/mcp-servers" element={<ToolList />} />
                  <Route path="tools/mcp-servers/new" element={<McpServerForm />} />
                  <Route path="tools/mcp/:serverId" element={<McpServerDetail />} />
                  <Route path="tools/mcp/:serverId/edit" element={<McpServerForm />} />
                  <Route path="tools/org-mcp/:serverId" element={<OrgMcpServerView />} />
                  <Route path="tools/new" element={<ToolForm />} />
                  <Route path="t/:toolId" element={<ToolDetail />} />
                  <Route path="t/:toolId/edit" element={<ToolForm />} />

                  <Route path="kbs" element={<SpaceKnowledgeBasesPage />} />
                  <Route path="kbs/:kbId" element={<SpaceKnowledgeBasesPage />} />
                  <Route path="kbs/:kbId/p/*" element={<SpaceKnowledgeBasesPage />} />
                  <Route path="kbs/:kbId/f/:fileId" element={<SpaceKnowledgeBasesPage />} />

                  <Route path="connections/emails" element={<EmailList />} />
                  <Route path="connections/emails/new" element={<EmailForm />} />
                  <Route path="connections/em/:emailId" element={<EmailDetail />} />
                  <Route path="connections/em/:emailId/edit" element={<EmailForm />} />

                  <Route path="integrations" element={<IntegrationList />} />
                  <Route path="integrations/new" element={<IntegrationForm />} />
                  <Route path="i/:integrationId" element={<IntegrationDetail />} />
                  <Route path="sub/:integrationId" element={<SubscriptionDetail />} />
                  <Route path="google" element={<GoogleConnect />} />

                  {/* Sandbox rendered persistently inside SpaceProviderRoute */}
                  <Route path="sandbox" element={null} />
                  <Route path="sandbox/:agentId" element={null} />

                  <Route path="conversations" element={<ConversationList />} />
                  <Route path="conv/:conversationId" element={<ConversationList />} />

                  <Route path="space" element={<SpaceManagePage />} />
                  <Route path="space/members" element={<SpaceManagePage />} />
                  <Route path="space/auto-join" element={<SpaceManagePage />} />
                  <Route path="space/labels" element={<SpaceManagePage />} />
                </Route>

              </Route>

              {/* Catch-all */}
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </ErrorBoundary>
              {/* Diagnostics overlay — renders only when the event-debug flag
                  is on (localStorage 'optr:debug:events' or ?evtdbg=1). */}
              <EventDebugHud />
            </ToastProvider>
          </AuthProvider>
        </AudioThemeProvider>
      </HotkeysProvider>
    </ThemeProvider>
  );
}
